import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { TAG_VARIANTS } from "./tag.constants";
import type { TagProps } from "./tag.types";

/**
 * Tag component for displaying status labels like Approve, Review, Reject.
 *
 * @example
 * Basic usage with default label
 * <Tag variant="approve" />
 *
 * @example
 * Override label with number
 * <Tag variant="approve">888</Tag>
 *
 * @example
 * With icon
 * <Tag variant="approve" endIcon={<SomeIcon />}>Approved</Tag>
 *
 * @example
 * Custom color schema
 * <Tag variant="approve" colorSchema={{ background: '#e0f7fa', text: '#00796b' }}>Custom</Tag>
 */
const Tag: FC<TagProps> = ({
	variant,
	children,
	startIcon,
	endIcon,
	colorSchema,
	className = "",
}) => {
	const variantConfig = TAG_VARIANTS[variant] ?? TAG_VARIANTS.primary;
	const colors = colorSchema ?? variantConfig.colors;
	const label = children ?? variantConfig.label;

	return (
		<span
			className={twMerge(
				"inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-[10px] py-[6px] font-bold text-xs",
				className,
			)}
			style={{
				backgroundColor: colors.background,
				color: colors.text,
			}}
		>
			{startIcon && (
				<span className="inline-flex items-center justify-center [&>svg]:size-[1.2em] [&>svg]:fill-current">
					{startIcon}
				</span>
			)}
			<span style={{ color: colors.text }}>{label}</span>
			{endIcon && (
				<span className="inline-flex items-center justify-center [&>svg]:size-[1.2em] [&>svg]:fill-current">
					{endIcon}
				</span>
			)}
		</span>
	);
};

export { Tag };
